// The live event streams stay open for the whole broadcast. The API server speaks HTTP/1.1, where the
// browser opens at most 6 connections per host, so the streams can be served from a host of their own
// (VUE_APP_LIVE_HOST_DOMAIN) to leave every connection to the API host free for the regular requests
const LIVE_HOST = process.env.VUE_APP_LIVE_HOST_DOMAIN || process.env.VUE_APP_API_HOST_DOMAIN

const RETRY_MIN_MS = 1000
const RETRY_MAX_MS = 15000

// Every event of a match (score, timer, player, team, screen) comes on one stream
export function liveEventsUrl(matchId) {
    return `${LIVE_HOST}/events/${matchId}/subscribe`
}

// One connection per match, shared by everyone that subscribes to it
const connections = {}

function connect(connection) {
    const source = new EventSource(liveEventsUrl(connection.matchId))
    connection.source = source

    Object.keys(connection.listeners).forEach(type => {
        source.addEventListener(type, event => {
            let data
            try {
                data = JSON.parse(event.data)
            } catch (e) {
                console.error(`Live events: unreadable ${type} event`, event.data)
                return
            }
            connection.listeners[type].forEach(listener => listener(data))
        })
    })

    // The stream opens with the current score and clock, so whatever was missed while it was down is caught up
    source.onopen = () => {
        connection.retryMs = RETRY_MIN_MS
        connection.openListeners.forEach(listener => listener())
    }

    source.onerror = () => {
        // The browser retries a dropped connection by itself (CONNECTING); a refused one (CLOSED) is retried here
        if (source.readyState !== EventSource.CLOSED || connection.closed) return
        console.warn(`Live events: the stream closed, retrying in ${connection.retryMs}ms`)
        connection.retryTimer = setTimeout(() => {
            connection.retryTimer = null
            if (!connection.closed) connect(connection)
        }, connection.retryMs)
        connection.retryMs = Math.min(connection.retryMs * 2, RETRY_MAX_MS)
    }
}

// handlers: { score, timer, player, team, screen } (each optional, gets the parsed event data), and onOpen
// for every (re)connection. Returns the unsubscribe function; the last one out closes the connection
export function subscribeLiveEvents(matchId, handlers) {
    const key = String(matchId)
    let connection = connections[key]
    if (!connection) {
        connection = {
            matchId: key,
            listeners: { score: [], timer: [], player: [], team: [], screen: [] },
            openListeners: [],
            subscribers: 0,
            retryMs: RETRY_MIN_MS,
            retryTimer: null,
            source: null,
            closed: false
        }
        connections[key] = connection
    }

    Object.keys(connection.listeners).forEach(type => {
        if (handlers[type]) connection.listeners[type].push(handlers[type])
    })
    if (handlers.onOpen) connection.openListeners.push(handlers.onOpen)
    connection.subscribers++

    // the listeners are in place before the stream opens, so nobody misses its opening events
    if (!connection.source) connect(connection)

    let subscribed = true
    return () => {
        if (!subscribed) return
        subscribed = false
        Object.keys(connection.listeners).forEach(type => {
            connection.listeners[type] = connection.listeners[type].filter(listener => listener !== handlers[type])
        })
        connection.openListeners = connection.openListeners.filter(listener => listener !== handlers.onOpen)
        connection.subscribers--
        if (connection.subscribers === 0) {
            connection.closed = true
            clearTimeout(connection.retryTimer)
            if (connection.source) connection.source.close()
            delete connections[key]
        }
    }
}
